import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Logo } from "~/components/icons/logo";
import { useUnauthenticated } from "~/hooks/use-unauthenticated";
import { api } from "~/utils/api";
import { cn } from "~/utils/cn";

function ForgotPasswordRequestSuccessful() {
  return (
    <div className="mx-auto my-10 max-w-sm">
      <div className="flex flex-col items-center">
        <Logo />
        <h2 className="mt-10 text-2xl font-semibold">Password reset</h2>
        <div className="mt-8 text-center">
          We&apos;ve sent you an email. Check your inbox and follow the link to
          set a new password.
        </div>
      </div>
    </div>
  );
}

export default function ForgotPasswordPage() {
  useUnauthenticated();

  // Set up form
  type FormValues = {
    email: string;
  };
  const {
    register,
    handleSubmit,
    formState: { isDirty, isSubmitting, isSubmitSuccessful, errors },
  } = useForm<FormValues>({
    resolver: zodResolver(
      z.object({
        email: z.string().email(),
      })
    ),
  });

  // Submit
  const requestResetPassword = api.user.requestResetPassword.useMutation();

  const onSubmit = handleSubmit((data) => {
    requestResetPassword.mutate({
      email: data.email,
    });
  });

  if (isSubmitSuccessful) {
    return <ForgotPasswordRequestSuccessful />;
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="mx-auto my-10 max-w-sm">
        <div className="flex flex-col items-center">
          <Logo />
          <h1 className="mt-10 text-center text-4xl font-semibold">
            Password reset
          </h1>

          <input
            {...register("email")}
            type="email"
            className={cn(
              "mt-8 block w-full rounded-md border-0 py-2 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#FC7569] sm:leading-6",
              errors.email && "ring-2 ring-red-500"
            )}
            placeholder="Enter your email address"
          />
          {errors.email?.message && (
            <p className="mt-2 w-full text-red-500">{errors.email.message}</p>
          )}

          <button
            type="submit"
            disabled={!isDirty || isSubmitting}
            className="mt-4 block w-full rounded-md bg-[#FC7569] py-2 px-4 font-bold text-white shadow-sm hover:bg-[#F56C5E] disabled:bg-gray-100 disabled:text-gray-400 sm:leading-6"
          >
            Reset my password
          </button>
        </div>
      </div>
    </form>
  );
}
